import type {
  LoadBalancingScheduleEmploymentType,
  LoadBalancingScheduleShiftPreference as LoadBalancingScheduleShiftPreferenceType,
  LoadBalancingShiftPatternPreference as LoadBalancingShiftPatternPreferenceType,
} from '@common/graphql';
import {
  sortSkillsByName,
  uniqueSkills,
  type WorkerSkillType,
} from './schedule-worker-skills-service';

export type TeamReviewTermInput = {
  id: string;
  active: boolean;
  effectiveStartDate: string;
  effectiveEndDate?: string | null;
  employmentType: LoadBalancingScheduleEmploymentType;
  role?: string | null;
  homeUnit?: string | null;
  homeUnitId?: string | null;
  ftePercentage?: number | null;
  preferredTime: LoadBalancingScheduleShiftPreferenceType;
  shiftPatternPreference: LoadBalancingShiftPatternPreferenceType;
  chargeEligible: boolean;
  skills?: readonly WorkerSkillType[] | null;
};

export type TeamReviewWorkerInput = {
  id: string;
  name: string;
  terms: TeamReviewTermInput[];
  periodSkills?: readonly { skill: WorkerSkillType }[] | null;
};

export type TeamReviewChange = {
  id: string;
  effectiveStartDate: string;
  effectiveEndDate: string | null;
  role: string | null;
  ftePercentage: number | null;
  preferredTime: LoadBalancingScheduleShiftPreferenceType;
  shiftPatternPreference: LoadBalancingShiftPatternPreferenceType;
  chargeEligible: boolean;
  active: boolean;
  skills: WorkerSkillType[];
};

export type TeamReviewWorker = {
  id: string;
  name: string;
  role: string | null;
  unit: string | null;
  // NOTE: unitId is what the Unit filter matches on; unit is the home-unit name, which is
  // display-only and not guaranteed unique.
  unitId: string | null;
  employmentType: LoadBalancingScheduleEmploymentType;
  shiftType: LoadBalancingScheduleShiftPreferenceType;
  shiftPattern: LoadBalancingShiftPatternPreferenceType;
  ftePercentage: number | null;
  chargeEligible: boolean;
  status: 'active' | 'inactive';
  upcomingChanges: TeamReviewChange[];
  skills: WorkerSkillType[];
};

const SHIFT_PREFERENCE_LABEL: Record<
  LoadBalancingScheduleShiftPreferenceType,
  string
> = {
  DAY: 'Day',
  NIGHT: 'Night',
  EITHER: 'Flexible',
};

const EMPLOYMENT_LABEL: Record<LoadBalancingScheduleEmploymentType, string> = {
  CORE: 'Core',
  AGENCY: 'Agency',
};

const SHIFT_PATTERN_LABEL: Record<
  LoadBalancingShiftPatternPreferenceType,
  string
> = {
  CONSECUTIVE: 'Consecutive',
  NON_CONSECUTIVE: 'Non-consecutive',
  NO_PREFERENCE: 'No Shift Pattern',
};

export function shiftPreferenceLabel(
  preference: LoadBalancingScheduleShiftPreferenceType
): string {
  return SHIFT_PREFERENCE_LABEL[preference];
}

export function employmentLabel(
  employmentType: LoadBalancingScheduleEmploymentType
): string {
  return EMPLOYMENT_LABEL[employmentType];
}

export function shiftPatternLabel(
  shiftPattern: LoadBalancingShiftPatternPreferenceType
): string {
  return SHIFT_PATTERN_LABEL[shiftPattern];
}

export function schedulingStatusLabel(active: boolean): string {
  return active ? 'Included' : 'Excluded';
}

export const FTE_FULL_TIME_PERCENTAGE = 100;

// NOTE: managers read and enter FTE as a decimal (1.0 = full-time), but the term stores a
// 0–100 percentage, so these convert across that one boundary rather than each call site.
export function fteDecimalFromPercentage(ftePercentage: number): number {
  return ftePercentage / FTE_FULL_TIME_PERCENTAGE;
}

export function ftePercentageFromDecimal(fteDecimal: number): number {
  return Math.round(fteDecimal * FTE_FULL_TIME_PERCENTAGE * 100) / 100;
}

export function fteMatchesRange(
  ftePercentage: number | null,
  min: number,
  max: number
): boolean {
  if (ftePercentage === null) return false;

  const fte = fteDecimalFromPercentage(ftePercentage);
  // NOTE: FTE ranges are bounded to 0–1; allow one machine epsilon for percentage conversion.
  return fte >= min - Number.EPSILON && fte <= max + Number.EPSILON;
}

export function fteLabel(ftePercentage: number | null): string {
  if (ftePercentage === null) return '—';
  // NOTE: the percentage carries at most one decimal, so the decimal has at most three; format
  // at that precision to avoid a raw float like 0.10099999999999999, then trim trailing zeros.
  const fte = fteDecimalFromPercentage(Math.round(ftePercentage * 10) / 10);
  const text = fte.toFixed(3).replace(/\.?0+$/, '');
  return text.includes('.') ? text : `${text}.0`;
}

export function skillsLabel(skills: readonly WorkerSkillType[]): string {
  return skills.length === 0
    ? '—'
    : skills.map((skill) => skill.name).join(', ');
}

export type TermCoverageInput = {
  effectiveStartDate: string;
  effectiveEndDate?: string | null;
  id?: string;
};

function sortByStart<T extends TermCoverageInput>(terms: readonly T[]): T[] {
  return [...terms].sort(
    (a, b) =>
      a.effectiveStartDate.localeCompare(b.effectiveStartDate) ||
      (a.id ?? '').localeCompare(b.id ?? '')
  );
}

// NOTE: strict coverage with no neighbouring-term fallback, mirroring the server's
// WorkerTerm.effective_on containment check, so an uncovered date fails closed.
export function findCoveringTerm<T extends TermCoverageInput>(
  terms: readonly T[],
  asOf: string
): T | undefined {
  // NOTE: term bounds are date-only ISO strings, so a lexical compare is chronological. An
  // ISO instant would break at the 'T', which sorts after '-', hence the slice.
  const on = asOf.slice(0, 10);
  return sortByStart(terms).find((term) => {
    const end = term.effectiveEndDate ?? null;
    return term.effectiveStartDate <= on && (end === null || end >= on);
  });
}

export function pickCurrentTerm<T extends TermCoverageInput>(
  terms: readonly T[],
  asOf: string
): T | undefined {
  if (terms.length === 0) return undefined;

  const covering = findCoveringTerm(terms, asOf);
  if (covering) return covering;

  const sorted = sortByStart(terms);
  const started = sorted.filter((term) => term.effectiveStartDate <= asOf);
  return started.length > 0 ? started[started.length - 1] : sorted[0];
}

function toChange(term: TeamReviewTermInput): TeamReviewChange {
  return {
    id: term.id,
    effectiveStartDate: term.effectiveStartDate,
    effectiveEndDate: term.effectiveEndDate ?? null,
    role: term.role ?? null,
    ftePercentage: term.ftePercentage ?? null,
    preferredTime: term.preferredTime,
    shiftPatternPreference: term.shiftPatternPreference,
    chargeEligible: term.chargeEligible,
    active: term.active,
    skills: sortSkillsByName(term.skills ?? []),
  };
}

export function toTeamReviewWorker(
  worker: TeamReviewWorkerInput,
  asOf: string
): TeamReviewWorker {
  const current = pickCurrentTerm(worker.terms, asOf);
  const upcomingChanges = sortByStart(worker.terms)
    .filter((term) => term.effectiveStartDate > asOf && term.id !== current?.id)
    .map(toChange);

  return {
    id: worker.id,
    name: worker.name,
    role: current?.role ?? null,
    unit: current?.homeUnit ?? null,
    unitId: current?.homeUnitId ?? null,
    employmentType: current?.employmentType ?? 'CORE',
    shiftType: current?.preferredTime ?? 'EITHER',
    shiftPattern: current?.shiftPatternPreference ?? 'NO_PREFERENCE',
    ftePercentage: current?.ftePercentage ?? null,
    chargeEligible: current?.chargeEligible ?? false,
    status: current?.active ? 'active' : 'inactive',
    upcomingChanges,
    skills: uniqueSkills(
      (worker.periodSkills ?? []).map((periodSkill) => periodSkill.skill)
    ),
  };
}
